<script setup lang="ts">
import UIcon from '~/components/ui/Icon.vue'
import ULink from '~/components/ui/Link.vue'

const props = defineProps({
    type: { type: String, default: 'button' },
    block: { type: Boolean, default: false },
    label: { type: String, default: null },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    padded: { type: Boolean, default: true },
    icon: { type: String, default: null },
    truncate: { type: Boolean, default: false },
    size: { type: String, default: () => 'sm' },
    loadingIcon: {
        type: String,
        default: () => {
            return 'Loading'
        },
    },
    leadingIcon: { type: String, default: null },
    trailingIcon: { type: String, default: null },
    trailing: { type: Boolean, default: false },
    leading: { type: Boolean, default: false },
    square: { type: Boolean, default: false },
})

const buttonClass = computed(() => {
    return props.block
        ? 'block w-full flex justify-center items-center'
        : 'inline-flex items-center'
})

const isLeading = computed(() => {
    return (
        (props.icon && props.leading) ||
        (props.icon && !props.trailing) ||
        (props.loading && !props.trailing) ||
        props.leadingIcon
    )
})

const isTrailing = computed(() => {
    return (
        (props.icon && props.trailing) ||
        (props.loading && props.trailing) ||
        props.trailingIcon
    )
})

const leadingIconName = computed(() => {
    if (props.loading) {
        return props.loadingIcon
    }

    return props.leadingIcon || props.icon
})

const trailingIconName = computed(() => {
    if (props.loading && !isLeading.value) {
        return props.loadingIcon
    }

    return props.trailingIcon || props.icon
})

const leadingIconClass = computed(() => {
    return props.loading
})

const trailingIconClass = computed(() => {
    return props.loading
})
</script>

<template>
    <ULink
        :type="type"
        :disabled="disabled || loading"
        class="button"
        :class="buttonClass"
    >
        <slot name="leading" :disabled="disabled" :loading="loading">
            <UIcon
                v-if="isLeading && leadingIconName"
                :name="leadingIconName"
                :class="leadingIconClass"
                :dynamic="true"
                aria-hidden="true"
            />
        </slot>

        <slot>
            <span
                v-if="label"
                :class="[truncate ? 'text-left break-all line-clamp-1' : '']"
            >
                {{ label }}
            </span>
        </slot>

        <slot name="trailing" :disabled="disabled" :loading="loading">
            <UIcon
                v-if="isTrailing && trailingIconName"
                :name="trailingIconName"
                :class="trailingIconClass"
                :dynamic="true"
                aria-hidden="true"
            />
        </slot>
    </ULink>
</template>

<style scoped></style>
